import React, { useEffect, useRef, useState } from 'react';
import {
  Send, Inbox, Shield, MessageCircle, FileText, RotateCcw, LogOut, AlertTriangle,
  Mail, Copy, Check, Trash2, Edit3, Undo2, VolumeX, Volume2, Heart, X,
} from 'lucide-react';
import { observeAuth, signInWithGoogle, signOutUser } from './firebase.js';
import { api } from './api.js';

const C = {
  bg: '#f4f0e6', surface: '#fbf8f1', paper: '#fdfbf5',
  text: '#2d2a24', muted: '#7d766a', faint: '#b3ac9e',
  accent: '#5e8d87', accentDark: '#4a7570', accentSoft: '#d7e3e0',
  bubbleUser: '#5e8d87', bubbleBot: '#ede7db',
  border: '#e0dacb', warning: '#a8683a', warningSoft: '#f5e6d8',
};

const QUICK_ACTIONS = [
  { label: 'how i feel', feelings: true },
  { label: 'body check', prompt: 'can we do a body check-in?' },
  { label: "i don't have words", prompt: "i don't know how to say what i'm feeling." },
  { label: "something's bugging me", prompt: 'something is bugging me.' },
  { label: 'just want to talk', prompt: 'i just want to talk about whatever.' },
];

const VIEW = { CHAT: 'chat', SHARED: 'shared', RULES: 'rules' };

const FEELINGS = [
  'mad', 'sad', 'worried', 'scared',
  'overwhelmed', 'embarrassed', 'lonely', 'weird',
  'numb', 'tired', 'proud', 'excited',
];

const INTENSITY_LABELS = ['a little', 'some', 'medium', 'a lot', 'huge'];

const SAY_SHORTER_PROMPT = 'can you say that in fewer words?';

function buildMailto(parentEmails, text, urgent) {
  if (!parentEmails?.length) return null;
  const subject = encodeURIComponent(urgent ? '[urgent] from moses' : 'a note from moses');
  const body = encodeURIComponent(text);
  return `mailto:${parentEmails.join(',')}?subject=${subject}&body=${body}`;
}

function composeFeelingsMessage(selected, intensity) {
  if (selected.length === 0) return '';
  const list = selected.length === 1 ? selected[0]
    : selected.length === 2 ? `${selected[0]} and ${selected[1]}`
    : `${selected.slice(0, -1).join(', ')}, and ${selected[selected.length - 1]}`;
  if (!intensity) return `i'm feeling ${list}.`;
  return `i'm feeling ${list}. how strong: ${INTENSITY_LABELS[intensity - 1]} (${intensity} out of 5).`;
}

function formatRelativeTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMin = Math.floor((now - d) / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24 && d.toDateString() === now.toDateString()) return `${diffHr}h ago · today`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return 'yesterday';
  if (diffDay < 7) return `${diffDay} days ago`;
  return d.toLocaleDateString();
}

export default function App() {
  const [user, setUser] = useState(undefined); // undefined = loading, null = signed out
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    let unsub;
    observeAuth(u => setUser(u || null))
      .then(fn => { unsub = fn; })
      .catch(e => setAuthError(e.message));
    return () => { if (unsub) unsub(); };
  }, []);

  if (user === undefined) return <Splash>loading…</Splash>;
  if (user === null) return <SignIn onSignIn={signInWithGoogle} error={authError} />;
  return <Chat user={user} />;
}

function Splash({ children }) {
  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.muted,
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {children}
    </div>
  );
}

function SignIn({ onSignIn, error }) {
  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <div style={{ maxWidth: 420, textAlign: 'center' }}>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '1.75rem', marginBottom: '1rem' }}>
          the quiet room
        </h1>
        <p style={{ color: C.muted, marginBottom: '2rem', lineHeight: 1.6 }}>
          sign in with the google account your dad set up for you.
        </p>
        <button onClick={onSignIn}
                style={{ background: C.accent, color: 'white', padding: '0.75rem 1.5rem',
                         borderRadius: '999px', border: 'none', cursor: 'pointer', fontSize: '1rem' }}>
          sign in with Google
        </button>
        {error && <p style={{ color: C.warning, marginTop: '1rem' }}>{error}</p>}
      </div>
    </div>
  );
}

function Chat({ user }) {
  const [state, setState] = useState(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState(VIEW.CHAT);
  const [error, setError] = useState(null);
  const [justListen, setJustListen] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.getState().then(setState).catch(e => setError(e.message));
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [state, loading]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || loading) return;
    setInput('');
    setError(null);

    // Just-listen mode: save without calling Claude, no bot reply.
    if (justListen) {
      const userMsg = { role: 'user', content: text, timestamp: new Date().toISOString() };
      setState(s => ({ ...s, recent: [...(s?.recent || []), userMsg] }));
      try {
        const res = await api.note(text);
        setState(s => ({ ...s, recent: res.recent, summary: res.summary }));
      } catch (e) {
        setError(e.message);
      }
      return;
    }

    setLoading(true);
    const userMsg = { role: 'user', content: text, timestamp: new Date().toISOString() };
    setState(s => ({ ...s, recent: [...(s?.recent || []), userMsg] }));
    try {
      const res = await api.sendMessage(text);
      setState(s => ({ ...s, recent: res.recent, summary: res.summary }));
    } catch (e) {
      setError(e.message);
      setState(s => ({ ...s, recent: [...s.recent, { role: 'assistant', content: 'connection glitched. try again?', isError: true }] }));
    } finally {
      setLoading(false);
    }
  };

  const respawn = async () => {
    await api.respawn();
    const fresh = await api.getState();
    setState(fresh);
  };

  const approveCard = async (idx, text) => {
    setState(s => ({
      ...s,
      recent: s.recent.map((m, i) => i === idx ? { ...m, pendingCard: null } : m),
    }));
    try {
      const res = await api.shareCard(text, false);
      setState(s => ({ ...s, shared: res.shared }));
      // Fire a mailto so the card actually reaches mom & dad. If the user
      // cancels the compose window, the card is still in the shared tab
      // and can be re-emailed from there.
      const mailto = buildMailto(state?.parentEmails, text, false);
      if (mailto) window.location.href = mailto;
    } catch (e) {
      setError(e.message);
    }
  };

  const discardCard = (idx) => {
    setState(s => ({
      ...s,
      recent: s.recent.map((m, i) => i === idx ? { ...m, pendingCard: null } : m),
    }));
  };

  const takeBack = async (timestamp) => {
    if (!timestamp) return;
    try {
      const res = await api.takeBack(timestamp);
      setState(s => ({ ...s, recent: res.recent }));
    } catch (e) {
      setError(e.message);
    }
  };

  const sayShorter = () => send(SAY_SHORTER_PROMPT);

  if (!state) return <Splash>{error || 'loading conversation…'}</Splash>;

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text,
                  fontFamily: "system-ui, sans-serif", display: 'flex', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: 720, display: 'flex', flexDirection: 'column' }}>
        <Header view={view} setView={setView} shared={state.shared}
                onRespawn={respawn} email={user.email} onSignOut={signOutUser}
                justListen={justListen} onToggleListen={() => setJustListen(x => !x)} />

        {view === VIEW.CHAT && (
          <ChatView state={state} input={input} setInput={setInput}
                    loading={loading} error={error} send={send} scrollRef={scrollRef}
                    onApproveCard={approveCard} onDiscardCard={discardCard}
                    onTakeBack={takeBack} onSayShorter={sayShorter}
                    justListen={justListen} />
        )}
        {view === VIEW.SHARED && <SharedView state={state} setState={setState} />}
        {view === VIEW.RULES && <RulesView state={state} />}
      </div>
    </div>
  );
}

function Header({ view, setView, shared, onRespawn, email, onSignOut, justListen, onToggleListen }) {
  const [confirm, setConfirm] = useState(false);
  const handleRespawn = () => {
    if (!confirm) {
      setConfirm(true);
      setTimeout(() => setConfirm(false), 3500);
      return;
    }
    setConfirm(false);
    onRespawn();
  };
  const sharedCount = shared?.length || 0;
  const tab = (id, icon, label) => (
    <button onClick={() => setView(id)}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer',
                     padding: '0.5rem 0', borderBottom: view === id ? `2px solid ${C.accent}` : '2px solid transparent',
                     color: view === id ? C.text : C.muted, display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9rem' }}>
      {icon}<span>{label}</span>
    </button>
  );
  return (
    <header style={{ padding: '1.5rem 1.5rem 0.75rem', borderBottom: `1px solid ${C.border}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.75rem' }}>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '1.4rem', margin: 0 }}>the quiet room</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: C.muted, fontSize: '0.75rem' }}>
          <Shield size={12} /><span>{email}</span>
          <button onClick={onSignOut} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted }}>
            <LogOut size={12} />
          </button>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', gap: 18 }}>
          {tab(VIEW.CHAT, <MessageCircle size={14} />, 'talk')}
          {tab(VIEW.SHARED, <Inbox size={14} />, `for mom & dad${sharedCount ? ` (${sharedCount})` : ''}`)}
          {tab(VIEW.RULES, <FileText size={14} />, 'rules')}
        </div>
        {view === VIEW.CHAT && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button onClick={onToggleListen}
                    title={justListen ? 'bot is quiet — tap to turn replies back on' : 'tell the bot to just listen — it will stop replying'}
                    style={{ background: justListen ? C.accentSoft : 'transparent',
                             color: justListen ? C.accentDark : C.muted,
                             border: `1px solid ${justListen ? C.accent : 'transparent'}`,
                             padding: '0.25rem 0.6rem', borderRadius: 999, cursor: 'pointer',
                             fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 4 }}>
              {justListen ? <VolumeX size={12} /> : <Volume2 size={12} />}
              {justListen ? 'just listen (on)' : 'just listen'}
            </button>
            <button onClick={handleRespawn}
                    style={{ background: confirm ? C.warningSoft : 'transparent',
                             color: confirm ? C.warning : C.muted,
                             border: `1px solid ${confirm ? C.warning : 'transparent'}`,
                             padding: '0.25rem 0.6rem', borderRadius: 999, cursor: 'pointer',
                             fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 4 }}>
              <RotateCcw size={12} />{confirm ? 'tap again' : 'respawn'}
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

function ChatView({ state, input, setInput, loading, error, send, scrollRef,
                    onApproveCard, onDiscardCard, onTakeBack, onSayShorter, justListen }) {
  const [feelingsOpen, setFeelingsOpen] = useState(false);

  const onFeelingsSend = (composed) => {
    setFeelingsOpen(false);
    if (composed) send(composed);
  };

  return (
    <>
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '1.5rem',
                                    maxHeight: 'calc(100vh - 280px)' }}>
        <Bubble role="assistant" content="hey. i'm a bot — your dad set this up. nothing here goes anywhere unless you send it." />
        {state.recent.map((m, i) => (
          <Bubble key={i} role={m.role} content={m.content} card={m.pendingCard} error={m.isError}
                  timestamp={m.timestamp}
                  onApprove={m.pendingCard ? (text) => onApproveCard(i, text ?? m.pendingCard) : undefined}
                  onDiscard={m.pendingCard ? () => onDiscardCard(i) : undefined}
                  onTakeBack={onTakeBack}
                  onSayShorter={onSayShorter} />
        ))}
        {loading && <Bubble role="assistant" content="…" />}
        {error && <div style={{ color: C.warning, fontSize: '0.8rem', marginTop: 8 }}>{error}</div>}
      </div>

      <div style={{ padding: '1rem 1.5rem', borderTop: `1px solid ${C.border}`, background: C.surface }}>
        {state.parentEmails?.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <a href={`mailto:${state.parentEmails.join(',')}?subject=I need a parent now`}
               style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                        background: C.warningSoft, color: C.warning,
                        border: `1px solid ${C.warning}`, padding: '0.45rem 1rem',
                        borderRadius: 999, fontSize: '0.8rem', textDecoration: 'none', fontWeight: 600 }}>
              <AlertTriangle size={14} /> i need a parent now
            </a>
          </div>
        )}

        {feelingsOpen ? (
          <FeelingsPicker onSend={onFeelingsSend} onCancel={() => setFeelingsOpen(false)} disabled={loading} />
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            {QUICK_ACTIONS.map(qa => (
              <button key={qa.label}
                      onClick={() => qa.feelings ? setFeelingsOpen(true) : send(qa.prompt)}
                      disabled={loading}
                      style={{ background: qa.feelings ? C.accentSoft : C.surface,
                               border: `1px solid ${qa.feelings ? C.accent : C.border}`,
                               color: qa.feelings ? C.accentDark : C.text,
                               padding: '0.4rem 0.8rem', borderRadius: 999, fontSize: '0.75rem',
                               cursor: loading ? 'not-allowed' : 'pointer',
                               display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {qa.feelings && <Heart size={11} />}
                {qa.label}
              </button>
            ))}
          </div>
        )}

        {justListen && (
          <div style={{ marginBottom: 8, fontSize: '0.75rem', color: C.accentDark,
                        background: C.accentSoft, padding: '0.4rem 0.6rem', borderRadius: 8,
                        display: 'flex', alignItems: 'center', gap: 6 }}>
            <VolumeX size={12} /> just listen mode — type all you want, the bot is quiet.
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <textarea value={input} onChange={e => setInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder={justListen ? 'type into the void...' : 'type here...'}
                    rows={1} disabled={loading}
                    style={{ flex: 1, padding: '0.75rem', borderRadius: 16, resize: 'none',
                             background: C.paper, border: `1px solid ${C.border}`, color: C.text,
                             fontSize: '0.95rem', outline: 'none', minHeight: 44, fontFamily: 'inherit' }} />
          <button onClick={() => send()} disabled={loading || !input.trim()}
                  style={{ width: 44, height: 44, borderRadius: '50%', border: 'none',
                           background: loading || !input.trim() ? C.faint : C.accent,
                           color: 'white', cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
                           display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Send size={16} />
          </button>
        </div>
      </div>
    </>
  );
}

function FeelingsPicker({ onSend, onCancel, disabled }) {
  const [selected, setSelected] = useState([]);
  const [intensity, setIntensity] = useState(0);

  const toggle = (f) => {
    setSelected(s => {
      if (s.includes(f)) return s.filter(x => x !== f);
      if (s.length >= 3) return s;
      return [...s, f];
    });
  };

  const canSend = selected.length > 0 && !disabled;

  return (
    <div style={{ marginBottom: 12, padding: '0.75rem',
                  background: C.paper, border: `1px solid ${C.border}`, borderRadius: 12 }}>
      <div style={{ fontSize: '0.75rem', color: C.muted, marginBottom: 8 }}>
        pick up to 3. tap again to unpick.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 12 }}>
        {FEELINGS.map(f => {
          const on = selected.includes(f);
          return (
            <button key={f} onClick={() => toggle(f)} disabled={disabled}
                    style={{ background: on ? C.accent : C.surface,
                             color: on ? 'white' : C.text,
                             border: `1px solid ${on ? C.accent : C.border}`,
                             padding: '0.5rem 0.25rem', borderRadius: 10,
                             fontSize: '0.8rem', cursor: disabled ? 'not-allowed' : 'pointer' }}>
              {f}
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: '0.75rem', color: C.muted, marginBottom: 6 }}>
        how strong (optional)
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {[1, 2, 3, 4, 5].map(n => (
          <button key={n} onClick={() => setIntensity(n === intensity ? 0 : n)} disabled={disabled}
                  style={{ width: 34, height: 34, borderRadius: '50%',
                           background: n <= intensity ? C.accent : C.surface,
                           color: n <= intensity ? 'white' : C.text,
                           border: `1px solid ${n <= intensity ? C.accent : C.border}`,
                           cursor: disabled ? 'not-allowed' : 'pointer', fontSize: '0.85rem' }}>
            {n}
          </button>
        ))}
        {intensity > 0 && (
          <span style={{ color: C.muted, fontSize: '0.75rem', marginLeft: 4 }}>
            {INTENSITY_LABELS[intensity - 1]}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => canSend && onSend(composeFeelingsMessage(selected, intensity))}
                disabled={!canSend}
                style={{ background: canSend ? C.accent : C.faint, color: 'white',
                         border: 'none', padding: '0.5rem 1rem', borderRadius: 999,
                         cursor: canSend ? 'pointer' : 'not-allowed', fontSize: '0.85rem',
                         display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <Send size={14} /> send
        </button>
        <button onClick={onCancel}
                style={{ background: 'transparent', color: C.muted,
                         border: `1px solid ${C.border}`,
                         padding: '0.5rem 1rem', borderRadius: 999,
                         cursor: 'pointer', fontSize: '0.85rem' }}>
          back
        </button>
      </div>
    </div>
  );
}

function Bubble({ role, content, card, error, timestamp, onApprove, onDiscard, onTakeBack, onSayShorter }) {
  const isUser = role === 'user';
  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState(card || '');
  const [confirmTakeBack, setConfirmTakeBack] = useState(false);
  const takeBackTimer = useRef(null);

  useEffect(() => { setEditDraft(card || ''); setEditing(false); }, [card]);
  useEffect(() => () => { if (takeBackTimer.current) clearTimeout(takeBackTimer.current); }, []);

  const handleTakeBack = () => {
    if (!onTakeBack || !timestamp) return;
    if (!confirmTakeBack) {
      setConfirmTakeBack(true);
      takeBackTimer.current = setTimeout(() => setConfirmTakeBack(false), 3500);
      return;
    }
    if (takeBackTimer.current) clearTimeout(takeBackTimer.current);
    setConfirmTakeBack(false);
    onTakeBack(timestamp);
  };

  const actionBtn = {
    fontSize: '0.7rem', background: 'transparent', border: 'none',
    color: C.faint, cursor: 'pointer', padding: '0.2rem 0.4rem',
    display: 'inline-flex', alignItems: 'center', gap: 3,
  };

  const showTakeBack = isUser && onTakeBack && timestamp && !error;
  const showSayShorter = !isUser && onSayShorter && !card && !error && content && content !== '…';

  return (
    <div style={{ display: 'flex', flexDirection: 'column',
                  alignItems: isUser ? 'flex-end' : 'flex-start', marginBottom: 12 }}>
      <div style={{ maxWidth: '85%', background: isUser ? C.bubbleUser : C.bubbleBot,
                    color: isUser ? 'white' : C.text, padding: '0.75rem 1rem',
                    borderRadius: 16, opacity: error ? 0.7 : 1, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
        {content}
        {card && (
          <div style={{ marginTop: 12, padding: '0.75rem', background: C.paper,
                        border: `1.5px dashed ${C.accent}`, borderRadius: 12, color: C.text }}>
            <div style={{ fontSize: '0.7rem', color: C.accent, marginBottom: 6, fontWeight: 700 }}>
              SHARE CARD — DRAFT
            </div>
            {editing ? (
              <>
                <textarea value={editDraft} onChange={e => setEditDraft(e.target.value)}
                          rows={Math.max(3, editDraft.split('\n').length)}
                          style={{ width: '100%', padding: '0.5rem', borderRadius: 8,
                                   border: `1px solid ${C.border}`, background: C.surface,
                                   fontFamily: 'Georgia, serif', fontSize: '0.9rem',
                                   color: C.text, resize: 'vertical', outline: 'none' }} />
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  <button onClick={() => { const t = editDraft.trim(); if (t) onApprove?.(t); }}
                          disabled={!editDraft.trim()}
                          style={{ background: editDraft.trim() ? C.accent : C.faint, color: 'white',
                                   border: 'none', padding: '0.4rem 0.9rem', borderRadius: 999,
                                   fontSize: '0.8rem', cursor: editDraft.trim() ? 'pointer' : 'not-allowed',
                                   display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Check size={13} /> send this version
                  </button>
                  <button onClick={() => { setEditing(false); setEditDraft(card); }}
                          style={{ background: 'transparent', color: C.muted,
                                   border: `1px solid ${C.border}`,
                                   padding: '0.4rem 0.9rem', borderRadius: 999,
                                   fontSize: '0.8rem', cursor: 'pointer' }}>
                    cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontFamily: 'Georgia, serif', whiteSpace: 'pre-wrap' }}>{card}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  <button onClick={() => onApprove?.(card)}
                          style={{ background: C.accent, color: 'white', border: 'none',
                                   padding: '0.4rem 0.9rem', borderRadius: 999,
                                   fontSize: '0.8rem', cursor: 'pointer',
                                   display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Mail size={13} /> send to mom & dad
                  </button>
                  <button onClick={() => setEditing(true)}
                          style={{ background: 'transparent', color: C.text,
                                   border: `1px solid ${C.border}`,
                                   padding: '0.4rem 0.9rem', borderRadius: 999,
                                   fontSize: '0.8rem', cursor: 'pointer',
                                   display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Edit3 size={13} /> edit first
                  </button>
                  <button onClick={onDiscard}
                          style={{ background: 'transparent', color: C.muted,
                                   border: `1px solid ${C.border}`,
                                   padding: '0.4rem 0.9rem', borderRadius: 999,
                                   fontSize: '0.8rem', cursor: 'pointer',
                                   display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <X size={13} /> discard
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      {(showTakeBack || showSayShorter) && (
        <div style={{ marginTop: 2, display: 'flex', gap: 2 }}>
          {showTakeBack && (
            <button onClick={handleTakeBack}
                    title="forget this message and everything after it"
                    style={{ ...actionBtn, color: confirmTakeBack ? C.warning : C.faint }}>
              <Undo2 size={11} /> {confirmTakeBack ? 'tap again' : 'take back'}
            </button>
          )}
          {showSayShorter && (
            <button onClick={onSayShorter}
                    title="ask the bot to say that in fewer words"
                    style={actionBtn}>
              say shorter
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function SharedView({ state, setState }) {
  const [copiedId, setCopiedId] = useState(null);
  const emailEnabled = (state.parentEmails || []).length > 0;

  const remove = async (id) => {
    const prev = state.shared;
    setState(s => ({ ...s, shared: s.shared.filter(c => c.id !== id) }));
    try {
      const res = await api.removeShareCard(id);
      setState(s => ({ ...s, shared: res.shared }));
    } catch {
      setState(s => ({ ...s, shared: prev }));
    }
  };

  const copy = async (id, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(c => (c === id ? null : c)), 2000);
    } catch {}
  };

  const btn = {
    fontSize: '0.75rem', background: 'transparent',
    border: `1px solid ${C.border}`, padding: '0.25rem 0.6rem',
    borderRadius: 999, color: C.muted, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 4,
    textDecoration: 'none',
  };

  if (!state.shared?.length) {
    return <div style={{ padding: '3rem', textAlign: 'center', color: C.faint }}>
      nothing shared yet.
    </div>;
  }
  return (
    <div style={{ padding: '1.5rem', overflowY: 'auto' }}>
      <div style={{ background: C.paper, border: `1px solid ${C.border}`,
                    borderRadius: 12, padding: '1rem', marginBottom: 16,
                    color: C.muted, fontSize: '0.85rem', lineHeight: 1.6 }}>
        these are notes you've chosen to share. {emailEnabled ? 'tap email to send one to mom & dad, ' : ''}or tap copy. remove any you change your mind about.
      </div>
      {state.shared.slice().reverse().map(card => (
        <div key={card.id} style={{ background: card.urgent ? C.warningSoft : C.paper,
                                    border: `1px solid ${card.urgent ? C.warning : C.border}`,
                                    borderRadius: 12, padding: '1rem', marginBottom: 12 }}>
          {card.urgent && <div style={{ fontSize: '0.7rem', color: C.warning, fontWeight: 700, marginBottom: 6 }}>URGENT</div>}
          <div style={{ fontFamily: 'Georgia, serif', whiteSpace: 'pre-wrap' }}>{card.text}</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, flexWrap: 'wrap', gap: 8 }}>
            <span style={{ color: C.faint, fontSize: '0.75rem' }}>{formatRelativeTime(card.timestamp)}</span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {emailEnabled && (
                <a href={buildMailto(state.parentEmails, card.text, card.urgent)} style={btn}>
                  <Mail size={12} /> email
                </a>
              )}
              <button onClick={() => copy(card.id, card.text)} style={btn}>
                {copiedId === card.id ? <><Check size={12} /> copied</> : <><Copy size={12} /> copy</>}
              </button>
              <button onClick={() => remove(card.id)} style={btn}>
                <Trash2 size={12} /> remove
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function RulesView({ state }) {
  const [showPrompt, setShowPrompt] = useState(false);
  return (
    <div style={{ padding: '1.5rem', overflowY: 'auto' }}>
      <div style={{ background: C.accentSoft, color: C.accentDark, padding: '1rem',
                    borderRadius: 12, fontSize: '0.85rem', marginBottom: 16 }}>
        <strong>no secrets.</strong> this page shows what the bot was told and what it remembers.
      </div>
      <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '1.1rem' }}>what the bot remembers</h2>
      <div style={{ background: C.paper, border: `1px solid ${C.border}`,
                    padding: '1rem', borderRadius: 12, whiteSpace: 'pre-wrap',
                    color: state.summary ? C.text : C.muted, fontStyle: state.summary ? 'normal' : 'italic' }}>
        {state.summary || 'no notes yet.'}
      </div>
      <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '1.1rem', marginTop: 24 }}>
        what the bot was told
      </h2>
      <button onClick={() => setShowPrompt(x => !x)}
              style={{ marginBottom: 12, background: 'transparent', border: `1px solid ${C.border}`,
                       padding: '0.4rem 0.8rem', borderRadius: 999, cursor: 'pointer', fontSize: '0.8rem' }}>
        {showPrompt ? 'hide' : 'show'} the full text
      </button>
      {showPrompt && (
        <pre style={{ background: C.paper, border: `1px solid ${C.border}`, padding: '1rem',
                      borderRadius: 12, fontSize: '0.75rem', whiteSpace: 'pre-wrap',
                      maxHeight: 400, overflowY: 'auto', fontFamily: 'ui-monospace, monospace' }}>
          {state.systemPrompt}
        </pre>
      )}
    </div>
  );
}
